import { marked } from "marked"
import remend from "remend"

export type Block = {
  raw: string
  src: string
  mode: "full" | "live"
}

function refs(text: string) {
  return /^\[[^\]]+\]:\s+\S+/m.test(text) || /^\[\^[^\]]+\]:\s+/m.test(text)
}

function open(raw: string) {
  const match = raw.match(/^[ \t]{0,3}(`{3,}|~{3,})/)
  if (!match) return false
  const mark = match[1]
  if (!mark) return false
  const char = mark[0]
  const size = mark.length
  const last = raw.trimEnd().split("\n").at(-1)?.trim() ?? ""
  return !new RegExp(`^[\\t ]{0,3}${char}{${size},}[\\t ]*$`).test(last)
}

function heal(text: string) {
  return remend(text, { linkMode: "text-only" })
}

// remend is superlinear in the text it heals (a 40 KB paragraph of links takes
// ~0.8 s in V8), and the tail is healed on every tick. What it repairs — an
// unclosed `**`, a half-typed link — sits at the very end, so a tail this
// long streams unhealed instead: the marker shows raw until its closer
// arrives, which ordinary paragraphs, far shorter than this, never see.
const HEAL_LIMIT = 4096

// Top-level blocks only: blockTokens queues the inline pass and never runs it,
// which is where marked's cost lives. Undefined when the tokens before the last
// one do not tile the text exactly — then nothing here can be trusted to line
// up with a full parse.
function blocks(text: string) {
  const tokens = new marked.Lexer().blockTokens(text)
  const tail = tokens.findLastIndex((token) => token.type !== "space")
  const out: string[] = []
  let at = 0
  for (const token of tokens.slice(0, Math.max(tail, 0))) {
    if (!text.startsWith(token.raw, at)) return
    at += token.raw.length
    // Blank lines render nothing; they ride with the block before them so the
    // blocks still tile the text.
    if (token.type === "space" && out.length > 0) out[out.length - 1] += token.raw
    else out.push(token.raw)
  }
  // The last block comes from the text itself: while it ends the input, marked
  // trims trailing spaces off its raw (a list item mid-word).
  out.push(text.slice(at))
  return out
}

// While streaming, the blocks before the last one almost never change: text is
// only appended, and marked grows or merges into its last top-level token. They
// come back unhealed, each on its own, so the caller can cache their HTML and
// re-parse only the tail — the whole message used to be healed and parsed twice
// on every 24 ms tick. "Almost": a `3.` that first read as a paragraph rejoins
// the list above it, a closing `$$` swallows the paragraphs it spans. The split
// is recomputed from scratch each time, so that costs a cache miss, never a
// stale render.
export function stream(text: string, live: boolean) {
  if (!live) return [{ raw: text, src: text, mode: "full" }] satisfies Block[]
  const normal = text.replace(/\r\n?/g, "\n")
  // Reference definitions resolve across blocks, so they cannot be split.
  const list = refs(normal) ? undefined : blocks(normal)
  if (!list) return [{ raw: text, src: text.length > HEAL_LIMIT ? text : heal(text), mode: "live" }] satisfies Block[]
  const tail = list.at(-1)!
  return [
    ...list.slice(0, -1).map((raw) => ({ raw, src: raw, mode: "live" as const })),
    // An unfinished fence is code: healing would close markers inside it.
    { raw: tail, src: open(tail) || tail.length > HEAL_LIMIT ? tail : heal(tail), mode: "live" },
  ] satisfies Block[]
}
